import { tv } from '@ui/tv';

export type ImpersonationBannerMode = 'readOnly' | 'changesAllowed';

/**
 * A frame around the whole app with a tab hanging from its top edge. Read-only
 * sessions are amber; sessions that can change the member's data are red, so
 * the riskier mode is legible at a glance. Both sit above CDK overlays so the
 * session stays visible, and ending it reachable, over dialogs too.
 */
export const impersonationBannerVariants = tv({
  slots: {
    // rounded like a macOS window, so the line follows the window corner
    // instead of being clipped by it
    frame: 'pointer-events-none fixed inset-0 z-2000 rounded-xl border-3',
    // before/after are concave shoulders joining the tab to the frame's top
    // line, so the tab reads as part of the frame; --tab carries the mode color
    tab: `fixed top-0 left-1/2 z-2001 flex h-8 max-w-[calc(100vw-1.5rem)] -translate-x-1/2 items-center
      gap-2 rounded-b-2xl bg-(--tab) pr-1 pl-3 text-xs whitespace-nowrap sm:gap-3 sm:pr-1.5 sm:pl-4 sm:text-sm
      before:absolute before:top-0.75 before:right-[calc(100%-1px)] before:size-2.5
      before:bg-[radial-gradient(circle_at_0_100%,transparent_10px,var(--tab)_10.5px)]
      after:absolute after:top-0.75 after:left-[calc(100%-1px)] after:size-2.5
      after:bg-[radial-gradient(circle_at_100%_100%,transparent_10px,var(--tab)_10.5px)]`,
    muted: '',
    end: `inline-flex h-6 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg bg-zinc-950 px-2
      text-xs font-medium transition-colors hover:bg-zinc-800 disabled:cursor-default disabled:opacity-60`,
  },
  variants: {
    mode: {
      readOnly: {
        frame: 'border-warning-500',
        tab: '[--tab:var(--color-warning-500)] text-zinc-950',
        muted: 'text-zinc-950/65',
        end: 'text-warning-500',
      },
      changesAllowed: {
        frame: 'border-danger-500',
        tab: '[--tab:var(--color-danger-500)] text-zinc-50',
        muted: 'text-zinc-50/75',
        end: 'text-zinc-50',
      },
    },
  },
  defaultVariants: {
    mode: 'readOnly',
  },
});
